import React, { FC, useEffect, useState } from "react";
import { Button, Icon, IconName, Text } from "@telia/teddy";
import _find from "lodash/find";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch, useSelector } from "react-redux";
import ExtraSimButton from "./ExtraSimButton";
import { Alert } from "../../../../components";
import { CART_ITEM_TYPE, CUSTOMER_TYPE } from "../../../../../../constants";
import { EsimType, RootState } from "../../../../../../models";
import { getFeatureToggles } from "../../../../app/state/selectors";
import { getExtraSimPrices } from "../../../state/actions/addMemberActions";
import { addExtraSimToCart } from "../../../state/actions/cartActions";
import { isSubscriptionDraft } from "../../../state/selectors/cartSelectors";
import { isSubscriptionPrepaid as isSubscriptionPrepaidSelector } from "../../../state/selectors/subscriptionsSelectors";
import {
    getExtraDataSimPrice,
    getExtraTvillingSimPrice,
    getHasExistingExtraDataSim,
} from "../../../state/selectors/memberSelector";
import { getPhoneNumberCheckType } from "../../../state/selectors/phoneNumberSelectors";
import ExtraSimModal from "../../../views/AddMember/ExtraSimModal";
import RadioButtonCards, { Option } from "../../molecules/RadioButtonCard/RadioButtonCard";
import "./ChooseSimCard.less";

type SimType = "sim" | "esim" | "sim-existing";
type SimOptionType = "esim" | "esim-not" | "sim" | "sim-existing" | "DATASIM" | "TVILLINGSIM";

interface StateProps {
    extraDataSimPrice?: number;
    extraTvillingSimPrice?: number;
    hasExistingExtraDataSim?: boolean;
    isSubscriptionPrepaid: boolean;
}

interface Props extends StateProps {
    buttonIcon?: IconName;
    buttonIconPlacement?: "left" | "right";
    buttonLabel?: string;
    esimType: EsimType;
    getCartItemTypeBySku?: string;
    isEsimCompatible: boolean;
    isEsimOnly: boolean;
    isEsimSelected: boolean;
    isOnboarding?: boolean;
    isOrderExtraSimEnabled: boolean;
    onButtonClick: () => void;
    phoneNumber?: string;
    setPersonalDetailsValue: (key: string, value: string | string[] | boolean) => void;
    shouldShowSimOptions: boolean;
    subscriptionId: string;
}

const simOptions: Option[] = [
    {
        value: "esim",
        heading: "eSIM",
        headingIcon: <Icon className="choose-sim-card__environment-icon" size="lg" name="environment" />,
        description: (
            <Text mb="100" className="choose-sim-card__radio-button-card-description">
                Digitalt SIM-kort som er innebygget i enheten. Vi sender deg en e-post med lenke for aktivering. <br />
                <br /> Du slipper å vente på at SIM-kort sendes i posten. Ingen produksjon av plast-SIM og ingen
                logistikk gjør eSIM miljøvennlig.
            </Text>
        ),
        isDisabled: false,
    },
    {
        value: "esim-not",
        heading: "eSIM",
        headingIcon: <Icon className="choose-sim-card__environment-icon" size="lg" name="environment" />,
        description: (
            <Text mb="100" className="choose-sim-card__radio-button-card-description">
                Enheten støtter ikke eSIM.
            </Text>
        ),
        isDisabled: true,
    },
    {
        value: "sim",
        heading: "Fysisk SIM-kort",
        description: (
            <Text mb="100" className="choose-sim-card__radio-button-card-description">
                Et SIM-kort som du fysisk setter inn i din enhet.
            </Text>
        ),
        isDisabled: false,
    },
    {
        value: "sim-existing",
        heading: "Behold ditt nåværende SIM-kort",
        description: undefined,
        isDisabled: true,
    },
];

const ChooseSimCard: FC<Props> = ({
    isEsimOnly,
    isEsimSelected,
    isEsimCompatible,
    isOrderExtraSimEnabled,
    getCartItemTypeBySku,
    isSubscriptionPrepaid,
    shouldShowSimOptions,
    esimType,
    subscriptionId,
    phoneNumber,
    buttonLabel,
    buttonIcon,
    buttonIconPlacement,
    setPersonalDetailsValue,
    onButtonClick,
    extraDataSimPrice,
    extraTvillingSimPrice,
    hasExistingExtraDataSim,
    isOnboarding = false,
}) => {
    const [selectedSimType, setSelectedSimType] = useState<SimType>(isEsimSelected || isEsimOnly ? "esim" : "sim");
    const [showExtraSimModal, setShowExtraSimModal] = useState(false);
    const isDraft = useSelector((state: RootState) => isSubscriptionDraft(state, subscriptionId));
    const isSimOptionsForExistingEnabled = useSelector(
        (state: RootState) => !!getFeatureToggles(state).enableSimOptionsForExisting,
    );
    const dispatch = useDispatch();
    const selectedSimOptions: Option[] = [];
    const isWearable = getCartItemTypeBySku && CART_ITEM_TYPE.WEARABLE === getCartItemTypeBySku;
    const isHandset = getCartItemTypeBySku && CART_ITEM_TYPE.HANDSET === getCartItemTypeBySku;
    const phoneNumberCheckType = useSelector((state: RootState) => getPhoneNumberCheckType(state, subscriptionId));
    const isExistingCustomer =
        phoneNumberCheckType === CUSTOMER_TYPE.EXISTING || phoneNumberCheckType === CUSTOMER_TYPE.EXISTING_OTHER;

    const onSubmitEsimDetails = (selectedEsimType: SimType, prevType: EsimType): void => {
        setPersonalDetailsValue("isEsimSelected", selectedEsimType === "esim");
        setPersonalDetailsValue("esimType", prevType);
    };
    const simOption = (option: SimOptionType) => _find(simOptions, (item) => item.value === option) as Option;

    useEffect(() => {
        if ((!shouldShowSimOptions && !isEsimSelected) || (!shouldShowSimOptions && isDraft && !isEsimSelected)) {
            setSelectedSimType("sim-existing");
        }
    }, []);

    if (!isWearable) {
        if (shouldShowSimOptions) {
            if (!isEsimCompatible) {
                selectedSimOptions.push(simOption("esim-not"));
            } else {
                selectedSimOptions.push(simOption("esim"));
            }
            if (!isEsimOnly) selectedSimOptions.push(simOption("sim"));
        } else if (isSimOptionsForExistingEnabled) {
            if (!isEsimCompatible && !isEsimOnly) {
                selectedSimOptions.push(simOption("esim-not"));
            } else {
                selectedSimOptions.push(simOption("esim"));
            }
            if (!isEsimOnly) {
                // existing customer will only be offered an eSIM (if hw support esim)
                selectedSimOptions.push(simOption("sim-existing"));
                simOption("sim-existing").isDisabled = selectedSimType === "sim-existing";
            }
        }
    } else if (isWearable && !isExistingCustomer) {
        // customer order a watch but the sim is  not for the watch
        // the sim is for the customers mobile phone and needs a new sim card cause she is a new customer
        // we let new customer choose between eSIM (if hw supports esim) and physical SIM
        if (isEsimCompatible) {
            selectedSimOptions.push(simOption("esim"));
        }
        selectedSimOptions.push(simOption("sim"));
    } else if (isWearable && isExistingCustomer) {
        selectedSimOptions.push(simOption("sim-existing"));
    }

    const canOnlyAddExtraSim = _isEmpty(selectedSimOptions);

    return (
        <div className="choose-sim-card">
            {isWearable && isEsimCompatible && (
                <Alert kind="warning" className="choose-sim-card__alert-box">
                    <div id="phone-number-area-warning-content" className="paragraph paragraph--fineprint">
                        {!isExistingCustomer ? (
                            <>
                                Som ny Telia-kunde trenger du et SIM-kort til mobilen din, og her velger du hvilket
                                SIM-kort mobilen din støtter. Hvis du i tillegg vil ha klokken din på mobilnettet, kan
                                du installere eSIM på klokken via klokke-appen.
                            </>
                        ) : (
                            <>
                                Hvis du vil ha klokken din på mobilnettet, kan du installere eSIM på klokken via
                                klokke-appen.
                            </>
                        )}
                    </div>
                </Alert>
            )}
            {showExtraSimModal && isOrderExtraSimEnabled && phoneNumber && (
                <ExtraSimModal
                    onCancel={() => setShowExtraSimModal(false)}
                    onAdd={async (simType, isEsim) => {
                        await dispatch(addExtraSimToCart(simType, subscriptionId, isEsim, phoneNumber, isOnboarding));
                    }}
                    extraDataSimPrice={extraDataSimPrice!}
                    extraTvillingSimPrice={extraTvillingSimPrice!}
                    hasExistingExtraDataSim={hasExistingExtraDataSim}
                    isOnboarding={isOnboarding}
                />
            )}
            {isExistingCustomer && isHandset && (
                <Alert kind="info" className="choose-sim-card__alert-box">
                    <div id="phone-number-area-warning-content" className="paragraph paragraph--fineprint">
                        <>
                            <strong className="telia-sans-medium">Du trenger ikke bestille nytt SIM-kort her</strong>
                            <br />
                            Når du får din nye telefon, kan du bruke det SIM-kortet du allerede har.
                            {isEsimCompatible &&
                                " Om telefonen er eSIM-kompatibel kan du bestille eSIM (digitalt SIM-kort) direkte fra telefonen eller via Min Side - raskt og enkelt."}
                        </>
                    </div>
                </Alert>
            )}
            {canOnlyAddExtraSim && (
                <Text mb="100">
                    Vil du ha SIM til nettbrett eller smartklokke? Legg til ekstra her – eller gå videre.
                </Text>
            )}
            <div className="choose-sim-card__esim-add">
                <RadioButtonCards
                    selectedValue={selectedSimType}
                    setSelectedValue={(value): void => {
                        setSelectedSimType(value as SimType);
                    }}
                    options={selectedSimOptions}
                />
                {isOrderExtraSimEnabled && !isWearable && !isSubscriptionPrepaid && (
                    <ExtraSimButton
                        onClick={() => {
                            if (!isOnboarding) {
                                dispatch(getExtraSimPrices(subscriptionId, phoneNumber)).then(() =>
                                    setShowExtraSimModal(true),
                                );
                            } else {
                                setShowExtraSimModal(true);
                            }
                        }}
                    />
                )}

                <div className="add-member__step-button-container">
                    <Button
                        onClick={() => {
                            onSubmitEsimDetails(selectedSimType, esimType);
                            onButtonClick();
                        }}
                        className="add-member__step-button"
                    >
                        {buttonIconPlacement === "left" && buttonIcon && (
                            <Icon className="add-member__step-button-icon" name={buttonIcon} />
                        )}
                        {buttonLabel}
                        {buttonIconPlacement === "right" && buttonIcon && (
                            <Icon className="add-member__step-button-icon" name={buttonIcon} />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps: (state: RootState, ownProps: { subscriptionId: string }) => StateProps = (
    state,
    { subscriptionId },
) => ({
    extraDataSimPrice: getExtraDataSimPrice(state),
    extraTvillingSimPrice: getExtraTvillingSimPrice(state),
    hasExistingExtraDataSim: getHasExistingExtraDataSim(state),
    isSubscriptionPrepaid: isSubscriptionPrepaidSelector(state, subscriptionId),
});

export default connect(mapStateToProps)(ChooseSimCard);
